// src/utils/masterBackupSystem.js - Complete Database Backup & Restore
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc,
  writeBatch,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Master backup configuration - Add new collections here
const BACKUP_COLLECTIONS = [
  'teams',
  'users', 
  'responses',
  'tredes',
  'email_jobs',
  'content'
  // Add future collections here
];

const SUBCOLLECTIONS_MAP = {
  'teams': ['invites'] // teams/{teamId}/invites subcollection
};

/**
 * Create complete database snapshot
 * @returns {Promise<Object>} Backup data object
 */
export const createMasterBackup = async () => {
  try {
    console.log('🔄 Starting master backup...');
    const backupData = {
      backupMetadata: {
        timestamp: new Date().toISOString(),
        version: '1.0',
        collections: BACKUP_COLLECTIONS,
        createdBy: 'admin-backup-system'
      },
      collections: {}
    };

    // Export all main collections
    for (const collectionName of BACKUP_COLLECTIONS) {
      console.log(`📁 Backing up collection: ${collectionName}`);
      
      const collectionRef = collection(db, collectionName);
      const snapshot = await getDocs(collectionRef);
      const collectionData = {};

      // Export documents
      for (const docSnap of snapshot.docs) {
        const docData = docSnap.data();
        collectionData[docSnap.id] = docData;

        // Export subcollections if configured
        if (SUBCOLLECTIONS_MAP[collectionName]) {
          const subcollections = {};
          
          for (const subCollectionName of SUBCOLLECTIONS_MAP[collectionName]) {
            console.log(`📂 Backing up subcollection: ${collectionName}/${docSnap.id}/${subCollectionName}`);
            
            const subCollectionRef = collection(db, collectionName, docSnap.id, subCollectionName);
            const subSnapshot = await getDocs(subCollectionRef);
            const subCollectionData = {};
            
            subSnapshot.docs.forEach(subDoc => {
              subCollectionData[subDoc.id] = subDoc.data();
            });
            
            if (Object.keys(subCollectionData).length > 0) {
              subcollections[subCollectionName] = subCollectionData;
            }
          }
          
          if (Object.keys(subcollections).length > 0) {
            collectionData[docSnap.id]._subcollections = subcollections;
          }
        }
      }

      backupData.collections[collectionName] = collectionData;
      console.log(`✅ ${collectionName}: ${Object.keys(collectionData).length} documents backed up`);
    }

    const totalDocuments = Object.values(backupData.collections)
      .reduce((total, collection) => total + Object.keys(collection).length, 0);

    console.log(`🎉 Backup complete! ${totalDocuments} documents across ${BACKUP_COLLECTIONS.length} collections`);
    
    return {
      success: true,
      data: backupData,
      stats: {
        totalCollections: BACKUP_COLLECTIONS.length,
        totalDocuments,
        backupSize: JSON.stringify(backupData).length,
        timestamp: backupData.backupMetadata.timestamp
      }
    };

  } catch (error) {
    console.error('💥 Backup failed:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Download backup as JSON file
 * @param {Object} backupData - Backup data object
 * @param {string} filename - Optional filename
 */
export const downloadBackup = (backupData, filename) => {
  try {
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const defaultFilename = `teamscan-backup-${timestamp}.json`;
    
    const dataStr = JSON.stringify(backupData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || defaultFilename;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    
    console.log(`📥 Backup downloaded: ${filename || defaultFilename}`);
    return { success: true, filename: filename || defaultFilename };
    
  } catch (error) {
    console.error('💥 Download failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Parse uploaded backup file
 * @param {File} file - Uploaded backup file
 * @returns {Promise<Object>} Parsed backup data
 */
export const parseBackupFile = async (file) => {
  try {
    const text = await file.text();
    const backupData = JSON.parse(text);
    
    // Validate backup structure
    if (!backupData.backupMetadata || !backupData.collections) {
      throw new Error('Invalid backup file format');
    }
    
    const stats = {
      backupDate: backupData.backupMetadata.timestamp,
      version: backupData.backupMetadata.version,
      collections: Object.keys(backupData.collections),
      totalDocuments: Object.values(backupData.collections)
        .reduce((total, collection) => total + Object.keys(collection).length, 0)
    };
    
    console.log('📋 Backup file validated:', stats);
    
    return {
      success: true,
      data: backupData,
      stats
    };
    
  } catch (error) {
    console.error('💥 Backup file parsing failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Preview changes before restore
 * @param {Object} backupData - Backup data to compare
 * @returns {Promise<Object>} Change preview
 */
export const previewRestore = async (backupData) => {
  try {
    console.log('🔍 Analyzing restore changes...');
    const changes = {
      collectionsToAdd: [],
      collectionsToUpdate: [],
      documentsToAdd: {},
      documentsToUpdate: {},
      documentsToDelete: {},
      subcollectionsToProcess: {}
    };

    for (const [collectionName, backupCollection] of Object.entries(backupData.collections)) {
      // Check if collection exists
      const currentCollectionRef = collection(db, collectionName);
      const currentSnapshot = await getDocs(currentCollectionRef);
      
      const currentDocs = {};
      currentSnapshot.docs.forEach(doc => {
        currentDocs[doc.id] = true;
      });

      const backupDocIds = Object.keys(backupCollection);
      const currentDocIds = Object.keys(currentDocs);

      // Documents to add
      const docsToAdd = backupDocIds.filter(id => !currentDocs[id]);
      if (docsToAdd.length > 0) {
        changes.documentsToAdd[collectionName] = docsToAdd;
      }

      // Documents to update (existing in both)
      const docsToUpdate = backupDocIds.filter(id => currentDocs[id]);
      if (docsToUpdate.length > 0) {
        changes.documentsToUpdate[collectionName] = docsToUpdate;
      }

      // Documents to delete (exist currently but not in backup)
      const docsToDelete = currentDocIds.filter(id => !backupCollection[id]);
      if (docsToDelete.length > 0) {
        changes.documentsToDelete[collectionName] = docsToDelete;
      }

      // Check for subcollections
      const subcollectionCount = Object.values(backupCollection)
        .reduce((count, doc) => count + (doc._subcollections ? Object.keys(doc._subcollections).length : 0), 0);
      
      if (subcollectionCount > 0) {
        changes.subcollectionsToProcess[collectionName] = subcollectionCount;
      }
    }

    const summary = {
      totalChanges: 
        Object.values(changes.documentsToAdd).reduce((sum, arr) => sum + arr.length, 0) +
        Object.values(changes.documentsToUpdate).reduce((sum, arr) => sum + arr.length, 0) +
        Object.values(changes.documentsToDelete).reduce((sum, arr) => sum + arr.length, 0),
      collectionsAffected: Object.keys(backupData.collections).length
    };

    console.log('🔍 Restore preview complete:', summary);
    
    return {
      success: true,
      changes,
      summary
    };
    
  } catch (error) {
    console.error('💥 Restore preview failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Restore database from backup
 * @param {Object} backupData - Backup data to restore
 * @param {Object} options - Restore options
 * @returns {Promise<Object>} Restore result
 */
export const restoreFromBackup = async (backupData, options = {}) => {
  try {
    console.log('🔄 Starting database restore...');
    const {
      deleteExisting = true,  // Delete docs not in backup
      selectedCollections = null, // Restore specific collections only
      batchSize = 500 // Firestore batch limit
    } = options;

    const collectionsToRestore = selectedCollections || Object.keys(backupData.collections);
    let totalRestored = 0;
    const results = {};

    for (const collectionName of collectionsToRestore) {
      if (!backupData.collections[collectionName]) {
        console.warn(`⚠️ Collection ${collectionName} not found in backup`);
        continue;
      }

      console.log(`🔄 Restoring collection: ${collectionName}`);
      const collectionData = backupData.collections[collectionName];
      
      // Delete existing documents if requested
      if (deleteExisting) {
        const existingSnapshot = await getDocs(collection(db, collectionName));
        const batch = writeBatch(db);
        let batchCount = 0;

        for (const existingDoc of existingSnapshot.docs) {
          if (!collectionData[existingDoc.id]) {
            batch.delete(doc(db, collectionName, existingDoc.id));
            batchCount++;

            if (batchCount >= batchSize) {
              await batch.commit();
              console.log(`🗑️ Deleted batch of ${batchCount} documents from ${collectionName}`);
              batchCount = 0;
            }
          }
        }

        if (batchCount > 0) {
          await batch.commit();
          console.log(`🗑️ Deleted final batch of ${batchCount} documents from ${collectionName}`);
        }
      }

      // Restore documents
      let restoredCount = 0;
      for (const [docId, docData] of Object.entries(collectionData)) {
        const { _subcollections, ...cleanDocData } = docData;
        
        // Restore main document
        await setDoc(doc(db, collectionName, docId), cleanDocData);
        restoredCount++;

        // Restore subcollections
        if (_subcollections) {
          for (const [subCollectionName, subCollectionData] of Object.entries(_subcollections)) {
            for (const [subDocId, subDocData] of Object.entries(subCollectionData)) {
              await setDoc(
                doc(db, collectionName, docId, subCollectionName, subDocId), 
                subDocData
              );
            }
            console.log(`📂 Restored ${Object.keys(subCollectionData).length} documents in ${collectionName}/${docId}/${subCollectionName}`);
          }
        }
      }

      results[collectionName] = restoredCount;
      totalRestored += restoredCount;
      console.log(`✅ ${collectionName}: ${restoredCount} documents restored`);
    }

    console.log(`🎉 Restore complete! ${totalRestored} documents across ${collectionsToRestore.length} collections`);
    
    return {
      success: true,
      totalRestored,
      results,
      timestamp: new Date().toISOString()
    };

  } catch (error) {
    console.error('💥 Restore failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get backup statistics
 * @param {Object} backupData - Backup data to analyze
 * @returns {Object} Backup statistics
 */
export const getBackupStats = (backupData) => {
  try {
    const stats = {
      metadata: backupData.backupMetadata,
      collections: {},
      totals: {
        collections: 0,
        documents: 0,
        subcollections: 0,
        sizeBytes: JSON.stringify(backupData).length
      }
    };

    for (const [collectionName, collectionData] of Object.entries(backupData.collections)) {
      const collectionStats = {
        documents: Object.keys(collectionData).length,
        subcollections: 0,
        hasSubcollections: false
      };

      // Count subcollections
      Object.values(collectionData).forEach(doc => {
        if (doc._subcollections) {
          const subCount = Object.keys(doc._subcollections).length;
          collectionStats.subcollections += subCount;
          collectionStats.hasSubcollections = true;
        }
      });

      stats.collections[collectionName] = collectionStats;
      stats.totals.documents += collectionStats.documents;
      stats.totals.subcollections += collectionStats.subcollections;
    }

    stats.totals.collections = Object.keys(backupData.collections).length;
    stats.totals.sizeMB = (stats.totals.sizeBytes / 1024 / 1024).toFixed(2);

    return stats;

  } catch (error) {
    console.error('Stats calculation failed:', error);
    return null;
  }
};

/**
 * Compare two backups
 * @param {Object} backupA - First backup
 * @param {Object} backupB - Second backup  
 * @returns {Object} Comparison result
 */
export const compareBackups = (backupA, backupB) => {
  try {
    const comparison = {
      dateA: backupA.backupMetadata.timestamp,
      dateB: backupB.backupMetadata.timestamp,
      differences: {},
      summary: {
        changedCollections: 0,
        addedDocuments: 0,
        removedDocuments: 0,
        modifiedDocuments: 0
      }
    };

    // Compare each collection
    const allCollections = new Set([
      ...Object.keys(backupA.collections),
      ...Object.keys(backupB.collections)
    ]);

    for (const collectionName of allCollections) {
      const collectionA = backupA.collections[collectionName] || {};
      const collectionB = backupB.collections[collectionName] || {};

      const docsA = Object.keys(collectionA);
      const docsB = Object.keys(collectionB);

      const added = docsB.filter(id => !collectionA[id]);
      const removed = docsA.filter(id => !collectionB[id]);
      const modified = docsA.filter(id => {
        if (!collectionB[id]) return false;
        return JSON.stringify(collectionA[id]) !== JSON.stringify(collectionB[id]);
      });

      if (added.length > 0 || removed.length > 0 || modified.length > 0) {
        comparison.differences[collectionName] = { added, removed, modified };
        comparison.summary.changedCollections++;
        comparison.summary.addedDocuments += added.length;
        comparison.summary.removedDocuments += removed.length;
        comparison.summary.modifiedDocuments += modified.length;
      }
    }

    return comparison;

  } catch (error) {
    console.error('Backup comparison failed:', error);
    return null;
  }
};